import PropTypes from 'prop-types'
import React from 'react'

// Day colors mapping - matching colors from useSchedulingMapData.js
const DAY_COLORS = {
  Sunday: '#F97316',
  Monday: '#E879F9',
  Tuesday: '#A78BFA',
  Wednesday: '#34D399',
  Thursday: '#FB7185',
  Friday: '#FBBF24',
  Saturday: '#60A5FA',
}

// Function to generate the SVG as a string
export const getWeekDayIconSvg = ({ weekDays, size = 20, borderWidth = 1, borderColor = '#333333' }) => {
  // If no days provided, return empty
  if (!weekDays || weekDays.length === 0) {
    return ''
  }

  const radius = size / 2
  const innerRadius = radius - borderWidth

  // If only one day, render a full circle with that day's color
  if (weekDays.length === 1) {
    const dayColor = DAY_COLORS[weekDays[0]]

    return `<svg height="${size}" width="${size}" viewBox="0 0 ${size} ${size}" overflow="visible">
      <circle r="${radius}" cx="${radius}" cy="${radius}" fill="${dayColor}" stroke="${borderColor}" stroke-width="${borderWidth}" />
    </svg>`
  }

  // For multiple days, create slices
  const sliceAngle = 360 / weekDays.length // Calculate based on the number of selected days

  // Create SVG paths only for days that are in the weekDays array
  const slices = weekDays
    .map((day, index) => {
      const startAngle = index * sliceAngle
      const endAngle = (index + 1) * sliceAngle

      // Convert angles to radians
      const startRad = ((startAngle - 90) * Math.PI) / 180
      const endRad = ((endAngle - 90) * Math.PI) / 180

      // Calculate points
      const x1 = radius + innerRadius * Math.cos(startRad)
      const y1 = radius + innerRadius * Math.sin(startRad)
      const x2 = radius + innerRadius * Math.cos(endRad)
      const y2 = radius + innerRadius * Math.sin(endRad)

      // Create path for the slice
      const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1

      const pathData = [
        `M ${radius},${radius}`,
        `L ${x1},${y1}`,
        `A ${innerRadius},${innerRadius} 0 ${largeArcFlag} 1 ${x2},${y2}`,
        'Z',
      ].join(' ')

      return `<path d="${pathData}" fill="${DAY_COLORS[day]}" />`
    })
    .join('')

  return `<svg height="${size}" width="${size}" viewBox="0 0 ${size} ${size}" overflow="visible">
    <circle r="${radius}" cx="${radius}" cy="${radius}" fill="${borderColor}" />
    ${slices}
  </svg>`
}

const WeekDayIcon = ({ weekDays, size = 20, borderWidth = 1, borderColor = '#333333' }) => {
  // If no days provided, return empty
  if (!weekDays || weekDays.length === 0) {
    return null
  }

  const radius = size / 2
  const innerRadius = radius - borderWidth

  // If only one day, render a full circle with that day's color
  if (weekDays.length === 1) {
    const dayColor = DAY_COLORS[weekDays[0]]

    return (
      <svg height={size} overflow="visible" viewBox={`0 0 ${size} ${size}`} width={size}>
        <circle cx={radius} cy={radius} fill={dayColor} r={radius} stroke={borderColor} strokeWidth={borderWidth} />
      </svg>
    )
  }

  // For multiple days, create slices
  const sliceAngle = 360 / weekDays.length // Calculate based on the number of selected days

  // Create SVG paths only for days that are in the weekDays array
  const slices = weekDays.map((day, index) => {
    const startAngle = index * sliceAngle
    const endAngle = (index + 1) * sliceAngle

    // Convert angles to radians
    const startRad = ((startAngle - 90) * Math.PI) / 180
    const endRad = ((endAngle - 90) * Math.PI) / 180

    // Calculate points
    const x1 = radius + innerRadius * Math.cos(startRad)
    const y1 = radius + innerRadius * Math.sin(startRad)
    const x2 = radius + innerRadius * Math.cos(endRad)
    const y2 = radius + innerRadius * Math.sin(endRad)

    // Create path for the slice
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1

    const pathData = [
      `M ${radius},${radius}`,
      `L ${x1},${y1}`,
      `A ${innerRadius},${innerRadius} 0 ${largeArcFlag} 1 ${x2},${y2}`,
      'Z',
    ].join(' ')

    return <path key={day} d={pathData} fill={DAY_COLORS[day]} />
  })

  return (
    <svg height={size} overflow="visible" viewBox={`0 0 ${size} ${size}`} width={size}>
      <circle cx={radius} cy={radius} fill={borderColor} r={radius} />
      {slices}
    </svg>
  )
}

WeekDayIcon.propTypes = {
  weekDays: PropTypes.arrayOf(PropTypes.string).isRequired,
  size: PropTypes.number,
  borderWidth: PropTypes.number,
  borderColor: PropTypes.string,
}

export default React.memo(WeekDayIcon)
